import { assert } from '@deqo/foundation';
import { Component, ComponentTuple, property } from '../../../concepts/objects';
import { Matrix, MatrixTransformable } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { HasXY, Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import {
    Figure,
    GuideLine,
    Physical,
    PotentialInteraction,
    RelativeTo,
    TransformAction,
    TransformHints,
    TransformScope
} from './Physical';
import { Snappable } from './Snappable';
import { UIField } from './UIField';

export abstract class AbstractPhysical extends Component implements Physical {

    @property({ type: 'string', oldName: 'tag' })
    public name!: string;

    @property({ type: 'boolean', default: false })
    public locked!: boolean;

    @property({ type: 'number', default: 5 })
    public anchor: number;

    public get boundary() {
        return false;
    }

    public get parentPhysical(): Physical {
        return this.parent?.cast(Physical) || null;
    }

    public get uiFields(): readonly UIField[] {
        return this.fields as any;
    }

    protected abstract get parentTransform(): Matrix;

    protected get localBounds() {
        const { w, h } = this.size();
        return new Rect(0, 0, w, h);
    }

    public bounded(): boolean {
        if (this.parentPhysical?.boundary) {
            return true;
        }
        return this.parentPhysical?.bounded() ?? false;
    }

    public abstract createFigure(): Figure;

    public setFromFigure(figure: Figure) {
        const scope = new TransformScope([this], 'poly');
        const from = this.createFigure();
        this.applyTransformation(scope, from, figure);
    }

    public queryGuides(): GuideLine[] {
        if (this.is(Snappable)) {
            return [
                ...GuideLine.fromRect(this.bounds(RelativeTo.Model)),
            ];
        }
        return [];
    }

    public queryTransformActions(singular: boolean): TransformAction[] {
        return [];
    }

    public abstract queryTransformHints(singular: boolean): TransformHints;

    public abstract applyTransformation(scope: TransformScope, from: Figure, to: Figure): this;

    public moveBy(distX: number, distY: number): this;
    public moveBy(dist: Point): this;
    public moveBy(...input: any[]): this {
        if (input.length === 2) {
            return this.moveBy(Point.from(input));
        }
        else {
            const rp = this.position(RelativeTo.Parent);
            const x = input[0].x ?? rp.x;
            const y = input[0].y ?? rp.y;
            const from = this.createFigure();
            const to = Matrix.translate(x, y).apply(from);
            this.setFromFigure(to);
            return this;
        }
    }

    public moveTo(toX: number, toY: number): this;
    public moveTo(to: Partial<HasXY>): this;
    public moveTo(...input: any[]): this {
        if (input.length === 2) {
            return this.moveTo(Point.from(input));
        }
        else {
            const rp = this.position(RelativeTo.Parent);
            const x = input[0].x ?? rp.x;
            const y = input[0].y ?? rp.y;
            return this.moveBy(new Point(x, y).subtract(rp));
        }
    }

    public rotateBy(radians: number): this {
        const from = this.createFigure();
        const to = Matrix.rotate(radians).apply(from);
        return this.applyTransformation(new TransformScope(this, 'rotate'), from, to);
    }

    public rotateTo(radians: number): this {
        return this.rotateBy(radians - this.rotation(RelativeTo.Parent));
    }

    public transform(to: RelativeTo | Physical = RelativeTo.Parent): Matrix {

        if (to === RelativeTo.Parent) {
            return this.parentTransform;
        }

        if (to === RelativeTo.Model) {

            const ts = [this.parentTransform];
            let next = this.parentPhysical;

            while (!!next && next.type !== 'root') {
                // if (next.childrenMode === ChildrenMode.Contain) {
                ts.push(next.transform());
                // }
                next = next.parent?.as(Physical);
            }

            return ts.reduceRight((t, x) => t.multiply(x), Matrix.identity);
        }

        if (to instanceof Component) {
            if (to === this) {
                return Matrix.identity;
            }

            const ts = [this.parentTransform];
            let next = this.parentPhysical;

            while (!!next && next !== to) {
                // if (next.childrenMode === ChildrenMode.Contain) {
                ts.push(next.transform());
                // }
                next = next.parent.cast(Physical);
            }

            assert(to === next, `transform: cannot compute relative to ${to}`);
            return ts.reduceRight((t, x) => t.multiply(x), Matrix.identity);
        }

        throw new Error('Invalid arg');
    }

    public position(to: RelativeTo | Physical = RelativeTo.Parent): Point {
        return this.transform(to).apply(Point.origin);
    }

    public center(to: RelativeTo | Physical = RelativeTo.Parent): Point {
        return this.bounds(to).center();
    }

    public rotation(to: RelativeTo | Physical = RelativeTo.Parent): number {
        return this.transform(to).decompose()[1];
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds));
    }

    public bounds(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds));
    }

    public abstract outline(to?: RelativeTo | Physical): Polyline;

    public abstract path(to?: RelativeTo | Physical): Path;

    public abstract size(): Point;

    /**
     * Determines whether the specified point appears within the element border.  If not specified then the point is
     * assumed to be relative to the parent element.
     *
     * @param mpt Point relative to the model root.
     */
    public abstract test(mpt: Point, to?: RelativeTo): boolean;

    public abstract testInteraction({ type, mpt }: PotentialInteraction): [boolean, string];

    public flatten(includeSelf?: boolean) {
        // if (this.boundary) {
        //     return includeSelf ? [this] : [];
        // }
        return super.flatten(includeSelf);
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        return (this as Physical).toShallowTuple(to);
    }

    /**
     * Converts the specified model geometry into a geometry relative to this phyiscal.
     */
    public toRelative(mpt: Point): Point;
    public toRelative(mpts: readonly Point[]): Point[];
    public toRelative<T>(mg: MatrixTransformable<T>): T;
    public toRelative(input: any): any {
        return this.transform(RelativeTo.Model).inverse().apply(input);
    }

    protected modelLocalTransform() {
        const pp = this.parentPhysical;
        if (pp) return pp.transform(RelativeTo.Model).inverse();
        return Matrix.identity;
    }

    protected localModelTransform() {
        return this.modelLocalTransform().inverse();
    }

}
